import React, { useEffect, useState } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { type CalendarDate, parseDate } from "@internationalized/date";
import { Button, DateField, IconButton, SingleSelectField, Tooltip } from "@shoptet/ui";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { CampaignTypeFilter, FilterParamsT } from "../../pages/DashboardTabs/Statistics";

enum DateRange {
  CUSTOM = 0,
  TODAY = 1,
  YESTERDAY = 2,
  LAST_7_DAYS = 3,
  LAST_30_DAYS = 4,
  LAST_3_MONTHS = 5,
  LAST_6_MONTHS = 6,
  LAST_12_MONTHS = 7,
  LAST_WEEK = 8,
  LAST_MONTH = 9,
  LAST_YEAR = 10,
}

enum CompareTo {
  PREVIOUS_PERIOD = 1,
  SAME_PERIOD_LAST_YEAR = 2,
}

interface DateRangeConfig {
  dateFrom: string;
  dateTo: string;
}

type ExtendedDateFieldProps = React.ComponentProps<typeof DateField> & {
  maxValue?: CalendarDate;
  minValue?: CalendarDate;
};

const ExtendedDateField = (props: ExtendedDateFieldProps) => <DateField {...props} />;

const calculateDateRange = (range: DateRange): DateRangeConfig => {
  const now = DateTime.local();
  const yesterday = now.minus({ days: 1 });

  switch (range) {
    case DateRange.TODAY:
      return {
        dateFrom: now.startOf("day").toISO(),
        dateTo: now.endOf("day").toISO(),
      };
    case DateRange.YESTERDAY:
      return {
        dateFrom: yesterday.startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
    case DateRange.LAST_7_DAYS:
      return {
        dateFrom: now.minus({ days: 7 }).startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
    case DateRange.LAST_30_DAYS:
      return {
        dateFrom: now.minus({ days: 30 }).startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
    case DateRange.LAST_3_MONTHS:
      return {
        dateFrom: now.minus({ months: 3 }).startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
    case DateRange.LAST_6_MONTHS:
      return {
        dateFrom: now.minus({ months: 6 }).startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
    case DateRange.LAST_12_MONTHS:
      return {
        dateFrom: now.minus({ months: 12 }).startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
    case DateRange.LAST_WEEK: {
      const lastWeek = now.minus({ weeks: 1 });
      return {
        dateFrom: lastWeek.startOf("week").toISO(),
        dateTo: lastWeek.endOf("week").toISO(),
      };
    }
    case DateRange.LAST_MONTH: {
      const lastMonth = now.minus({ months: 1 });
      return {
        dateFrom: lastMonth.startOf("month").toISO(),
        dateTo: lastMonth.endOf("month").toISO(),
      };
    }
    case DateRange.LAST_YEAR: {
      const lastYear = now.minus({ years: 1 });
      return {
        dateFrom: lastYear.startOf("year").toISO(),
        dateTo: lastYear.endOf("year").toISO(),
      };
    }
    case DateRange.CUSTOM:
    default:
      return {
        dateFrom: now.minus({ days: 30 }).startOf("day").toISO(),
        dateTo: yesterday.endOf("day").toISO(),
      };
  }
};

export const PerformanceStatsFilterAdvanced = ({
  campaignStartDate,
  googleWizardFinished,
  metaWizardFinished,
  onCompareClear,
  onFilterChange,
}: {
  campaignStartDate: string;
  googleWizardFinished: boolean;
  metaWizardFinished: boolean;
  onCompareClear: () => void;
  onFilterChange: (params: FilterParamsT) => void;
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Stats" }) as {
    t: (key: string) => string;
  };

  const [campaignType, setCampaignType] = useState<CampaignTypeFilter>(CampaignTypeFilter.ALL);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [period, setPeriod] = useState<DateRange>(DateRange.LAST_30_DAYS);
  const [compareTo, setCompareTo] = useState<CompareTo | null>(null);

  const campaignTypeOptions = [
    {
      label: t("Campaign type all"),
      value: CampaignTypeFilter.ALL,
    },
    {
      label: t("Campaign type google"),
      value: CampaignTypeFilter.GOOGLE,
    },
    {
      label: t("Campaign type meta"),
      value: CampaignTypeFilter.META,
    },
  ];

  const periodSelectOptions = [
    {
      label: t("Period custom"),
      value: DateRange.CUSTOM,
    },
    {
      label: t("Period today"),
      value: DateRange.TODAY,
    },
    {
      label: t("Period yesterday"),
      value: DateRange.YESTERDAY,
    },
    {
      label: t("Period last 7 days"),
      value: DateRange.LAST_7_DAYS,
    },
    {
      label: t("Period last 30 days"),
      value: DateRange.LAST_30_DAYS,
    },
    {
      label: t("Period last 3 months"),
      value: DateRange.LAST_3_MONTHS,
    },
    {
      label: t("Period last 6 months"),
      value: DateRange.LAST_6_MONTHS,
    },
    {
      label: t("Period last 12 months"),
      value: DateRange.LAST_12_MONTHS,
    },
    {
      label: t("Period last week"),
      value: DateRange.LAST_WEEK,
    },
    {
      label: t("Period last month"),
      value: DateRange.LAST_MONTH,
    },
    {
      label: t("Period last year"),
      value: DateRange.LAST_YEAR,
    },
  ];

  const periodCompareOptions = [
    {
      label: "",
      value: 0,
    },
    {
      label: t("Compare to 1"),
      value: 1,
    },
    {
      label: t("Compare to 2"),
      value: 2,
    },
  ];

  const maxDate = DateTime.local().minus({ days: 1 }).toFormat("yyyy-MM-dd");

  const calculateComparisonDates = (from: string, to: string, compareType: CompareTo | null) => {
    if (compareType === null) {
      return null;
    }

    const fromDate = DateTime.fromISO(from);
    const toDate = DateTime.fromISO(to);
    const duration = toDate.diff(fromDate);

    if (compareType === CompareTo.PREVIOUS_PERIOD) {
      const comparisonTo = fromDate.minus({ milliseconds: 1 });
      const comparisonFrom = comparisonTo.minus(duration);
      return {
        comparisonDateFrom: comparisonFrom.toFormat("yyyy-MM-dd"),
        comparisonDateTo: comparisonTo.toFormat("yyyy-MM-dd"),
      };
    } else if (compareType === CompareTo.SAME_PERIOD_LAST_YEAR) {
      return {
        comparisonDateFrom: fromDate.minus({ years: 1 }).toFormat("yyyy-MM-dd"),
        comparisonDateTo: toDate.minus({ years: 1 }).toFormat("yyyy-MM-dd"),
      };
    }

    return null;
  };

  const handleDateFromChange = (date: string) => {
    setDateFrom(date);
    if (period !== DateRange.CUSTOM) {
      setPeriod(DateRange.CUSTOM);
    }
  };
  const submitFilter = () => {
    if (dateFrom && dateTo) {
      const comparisonDates = calculateComparisonDates(dateFrom, dateTo, compareTo);
      onFilterChange({
        campaignType,
        dateFrom,
        dateTo,
        ...comparisonDates,
      } as FilterParamsT);
    }
  };

  const clearFilter = () => {
    setCampaignType(CampaignTypeFilter.ALL);
    setPeriod(DateRange.LAST_30_DAYS);
    setCompareTo(null);
  };

  const toRACDateValue = (date: string | null | undefined): CalendarDate | null | undefined => {
    if (date === null) {
      return null;
    }

    if (date === undefined) {
      return undefined;
    }

    try {
      return parseDate(date);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (period !== DateRange.CUSTOM) {
      const range = calculateDateRange(period);

      const formattedDateFrom = DateTime.fromISO(range.dateFrom).toFormat("yyyy-MM-dd");
      const formattedDateTo = DateTime.fromISO(range.dateTo).toFormat("yyyy-MM-dd");

      setDateFrom(formattedDateFrom);
      setDateTo(formattedDateTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    if (!compareTo) {
      onCompareClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareTo]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "14px" }}>
        {googleWizardFinished && metaWizardFinished && (
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("Campaign type")}</div>
            <SingleSelectField
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              label={t("Campaign type")}
              options={campaignTypeOptions}
              value={campaignType}
              labelHidden
              onChange={(value) => value && setCampaignType(value as CampaignTypeFilter)}
            />
          </div>
        )}
        <div className="performance-stats-filter-item" style={{ flex: 1 }}>
          <div>{t("Select start date")}</div>
          <ExtendedDateField
            label={t("Select start date")}
            maxValue={toRACDateValue(maxDate) ?? undefined}
            minValue={toRACDateValue(campaignStartDate) ?? undefined}
            value={dateFrom}
            labelHidden
            onChange={(value) => value && handleDateFromChange(value)}
          />
        </div>
        <div className="performance-stats-filter-item" style={{ flex: 1 }}>
          <div>{t("Select end date")}</div>
          <ExtendedDateField
            label={t("Select end date")}
            maxValue={toRACDateValue(maxDate) ?? undefined}
            minValue={toRACDateValue(dateFrom) ?? undefined}
            value={dateTo}
            labelHidden
            onChange={(value) => value && setDateTo(value)}
          />
        </div>
        <div className="performance-stats-filter-item" style={{ flex: 1 }}>
          <div>{t("Select period")}</div>
          <SingleSelectField
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => JSON.stringify(option.value)}
            label={t("Select period")}
            options={periodSelectOptions}
            value={JSON.stringify(period)}
            labelHidden
            onChange={(value) => value && setPeriod(JSON.parse(value))}
          />
        </div>
        <div className="performance-stats-filter-item" style={{ flex: 1 }}>
          <div className="performance-stats-filter-item__tooltip">
            <span style={{ marginRight: "8px" }}>{t("Compare to period")}</span>
            <Tooltip content={t("Compare period tooltip")}>
              <IconButton icon="shp-info" size="sm" ghost />
            </Tooltip>
          </div>
          <SingleSelectField
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => JSON.stringify(option.value)}
            label={t("Compare to period")}
            options={periodCompareOptions}
            value={JSON.stringify(compareTo)}
            labelHidden
            onChange={(value) => value && setCompareTo(JSON.parse(value))}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexDirection: "row",
          margin: "24px 0 36px",
        }}
      >
        <Button size="sm" type="button" variant="action" onClick={submitFilter}>
          {t("Filter")}
        </Button>
        <IconButton
          icon="shp-cross"
          size="sm"
          style={{ marginLeft: "10px" }}
          type="button"
          variant="primary"
          onClick={clearFilter}
        />
      </div>
    </div>
  );
};
